import type { ModelLayerSpecification, Map } from "@iventis/mapbox-gl";
import type { Feature, Point, Polygon } from "geojson";
import type { MapObjectProperties } from "@iventis/map-types/src/map-object-properties";
import { ModelStyle } from "@iventis/domain-model/model/modelStyle";
import { SystemDataFieldName } from "@iventis/domain-model/model/systemDataFieldName";
import { DataField } from "@iventis/domain-model/model/dataField";
import buffer from "@turf/buffer";
import { getStaticStyleValue } from "@iventis/layer-style-helpers";
import { BehaviorSubject, Subscription } from "rxjs";
import type { ModelData, ModelDataStore } from "../../data-store/model-data-store";
import type { MapModuleLayer, MapState } from "../../types/store-schema";
import { MapboxModelCustomisableLayer } from "./mapbox-model-customisable-layer";
import { MapboxModelStandBaseLayer } from "./mapbox-model-stand-base-layer";
import { MapboxModelStandStemLayer } from "./mapbox-model-stand-stem-layer";
import { StylePropertyToValueMapV2 } from "../../types/style-types";
import { DataFieldListItemStore } from "../../data-store/data-field-list-item-store";
import { ApplyListItemTextFunction } from "../mapbox/engine-mapbox-types-and-constants";
import { MapboxModelTextLayer } from "./mapbox-model-text-layer";

export class MapboxModelLayer extends MapboxModelCustomisableLayer<Feature<Point, MapObjectProperties>[], ModelStyle> {
    private objectIdsToShowStandLayer: string[] = [];

    private standBaseMapboxLayer: MapboxModelStandBaseLayer;

    private standStemMapboxLayer: MapboxModelStandStemLayer;

    private textMapboxLayer: MapboxModelTextLayer;

    private altitudeDataField: DataField;

    private previousStateStamps: Pick<Record<keyof MapState, string>, "mapObjectsSelected" | "geoJSON"> = { mapObjectsSelected: null, geoJSON: null };

    private stateSubscription: Subscription;

    private readonly preview: boolean;

    private applyTextFieldExpression: ApplyListItemTextFunction;

    constructor(
        map: Map,
        layer: MapModuleLayer,
        state$: BehaviorSubject<MapState>,
        modelDataStore: ModelDataStore,
        dataFieldListItemDataStore: DataFieldListItemStore,
        events: {
            getAboveLayerId: (layerId: string) => string;
            applyTextFieldExpression?: ApplyListItemTextFunction;
        },
        preview: boolean
    ) {
        super(map, layer, state$, modelDataStore, dataFieldListItemDataStore, events);
        this.preview = preview;
        this.applyTextFieldExpression = events.applyTextFieldExpression;
        if (!preview) {
            // Altitude datafield will never change so get it at the start
            this.altitudeDataField = this.state$.value.projectDataFields.find((pdf) => pdf.systemDataFieldName === SystemDataFieldName.ModelAltitude);
            if (this.altitudeDataField == null) {
                throw new Error("ModelAltitude system data field not found in projectDataFields.");
            }
        }
        this.onSelectedListener();
    }

    public async initaliseLayer(layer: MapModuleLayer): Promise<void> {
        await super.initaliseLayer(layer);

        // Due to awaiting before we need to ensure the layer has not been removed before adding additional layers
        if (!this.preview && !this.removed) {
            // Any model map object with an altitude will need a "Stand" which is made up of a base and a stem
            this.standBaseMapboxLayer = new MapboxModelStandBaseLayer(this.state$, this.layerId, this.map, { name: layer.name, type: "stand-base" });
            this.standStemMapboxLayer = new MapboxModelStandStemLayer(this.state$, this.layerId, this.map, { name: layer.name, type: "stand-stem" });
            this.textMapboxLayer = new MapboxModelTextLayer(
                this.map,
                layer,
                this.state$,
                this.altitudeDataField,
                this.dataFieldListItemStore,
                {
                    getAboveLayerId: this.getAboveLayerId,
                    applyTextFieldExpression: this.applyTextFieldExpression,
                },
                this.preview
            );
            this.objectIdsToShowStandLayer = this.getSelectedMapObjects().map((object) => object.objectId);
            this.getLatestMapObjectsWithAltitude();
        }
        if (!this.removed) {
            this.textMapboxLayer?.addLayer();
        }
    }

    public iventisLayerToMapboxLayer(iventisLayer: MapModuleLayer, modelData: ModelData[]): ModelLayerSpecification {
        const mapboxLayer = super.iventisLayerToMapboxLayer(iventisLayer, modelData) as ModelLayerSpecification;
        // Add the map object altitude value to the layer's altitude value
        mapboxLayer.paint["model-translation"] = this.createModelAltitudeExpression(iventisLayer.modelStyle.modelAltitude);
        return mapboxLayer;
    }

    public updateLocalSource(source: Feature<Point, MapObjectProperties>[], drawingSource?: Feature<Point, MapObjectProperties>[]) {
        super.updateLocalSource(source);
        // When drawing source is not null use that as it will have only the features we need to show a base for
        if (drawingSource != null) {
            const layerAltitudeStyleValue = getStaticStyleValue(this.getIventisLayer().modelStyle.modelAltitude);
            if (layerAltitudeStyleValue > 0) {
                this.objectIdsToShowStandLayer = drawingSource.map((feature) => feature.properties.id);
            } else {
                this.objectIdsToShowStandLayer = drawingSource.flatMap((feature) => ((feature.properties[this.altitudeDataField.id] as number) > 0 ? [feature.properties.id] : []));
            }
            this.updateStandPosition(drawingSource);
            this.textMapboxLayer?.updateLocalSource(source);
            return;
        }
        this.updateStandPosition(source);
        this.textMapboxLayer?.updateLocalSource(source);
    }

    public async updateStyle(styleChanges: StylePropertyToValueMapV2<ModelStyle>): Promise<void> {
        await super.updateStyle(styleChanges);
        if (styleChanges.modelAltitude) {
            this.mapboxLayerManager.setPaintProperty("model-translation", this.createModelAltitudeExpression(styleChanges.modelAltitude));
            this.textMapboxLayer?.updateModelStyle(styleChanges);
            this.getLatestMapObjectsWithAltitude();
        }

        if (styleChanges.height) {
            this.textMapboxLayer?.updateModelStyle(styleChanges);
        }

        await this.textMapboxLayer?.updateStyle(styleChanges);

        // Update the stem mapbox layer with the same style changes
        this.standStemMapboxLayer?.updateStyle(styleChanges);
    }

    // Pass lifecycle updates to the text layer so it can update its Mapbox layer accordingly
    public updateMapLevel(level: number): void {
        super.updateMapLevel(level);
        this.textMapboxLayer?.updateMapLevel(level);
    }

    public addDateFilter(dataFields: DataField[], day: number, time: number) {
        super.addDateFilter(dataFields, day, time);
        this.textMapboxLayer?.addDateFilter(dataFields, day, time);
    }

    public removeDateFilter() {
        super.removeDateFilter();
        this.textMapboxLayer?.removeDateFilter();
    }

    private updateStandPosition(geojson: Feature<Point, MapObjectProperties>[]) {
        const drawingSourcePoint: Feature<Point>[] = [];
        const drawingSourceHeight: Feature<Polygon, Record<string, number>>[] = [];

        this.objectIdsToShowStandLayer.forEach((objectId) => {
            const foundObject = geojson.find((s) => s.properties.id === objectId);
            if (foundObject != null) {
                // Set position of the base
                drawingSourcePoint.push(foundObject);
                // Create geometry for the stem
                const heightFeature = buffer(foundObject, 10, { units: "centimeters", steps: 10 });
                // Set height for the stem
                heightFeature.properties = { [this.altitudeDataField.id]: foundObject.properties[this.altitudeDataField.id] ?? 0 };
                drawingSourceHeight.push(heightFeature);
            }
        });

        this.standBaseMapboxLayer?.updateLocalSource(drawingSourcePoint);
        this.standStemMapboxLayer?.updateLocalSource(drawingSourceHeight);
    }

    /** Listens to the selected map object state value has changed */
    private onSelectedListener() {
        this.stateSubscription = this.state$.subscribe((state) => {
            const { mapObjectsSelected, geoJSON } = state;

            if (this.previousStateStamps.mapObjectsSelected !== state.mapObjectsSelected.stamp) {
                this.getLatestMapObjectsWithAltitude();
            }

            if (this.previousStateStamps.geoJSON !== state.geoJSON.stamp) {
                this.getLatestMapObjectsWithAltitude();
            }

            this.previousStateStamps = {
                mapObjectsSelected: mapObjectsSelected.stamp,
                geoJSON: geoJSON.stamp,
            };
        });
    }

    private getLatestMapObjectsWithAltitude() {
        const iventisLayer = this.getIventisLayer();
        const geoJsonSource = this.getGeoJson();

        // Ensure the layer exists in the state before we update the stand layers
        if (iventisLayer == null || geoJsonSource == null) {
            return;
        }

        const modelStyleAltitudeValue = getStaticStyleValue(iventisLayer.modelStyle.modelAltitude) ?? 0;

        // Get all map objects that have an altitude more than 0
        const mapObjectsNeedingStand = this.getSelectedMapObjects().flatMap((selectedMapObject) => {
            const geojson = geoJsonSource.find((feature) => feature.properties.id === selectedMapObject.objectId);
            const mapObjectAltitudeValue = geojson?.properties[this.altitudeDataField.id] ?? 0;
            // If either the map object altitude or layer altitude is not 0 then we need a "stand" on the model
            if (mapObjectAltitudeValue !== 0 || modelStyleAltitudeValue !== 0) {
                return [geojson];
            }
            return [];
        });

        this.objectIdsToShowStandLayer = mapObjectsNeedingStand.map((feature) => feature.properties.id);
        this.updateStandPosition(mapObjectsNeedingStand);
        this.textMapboxLayer?.updateLocalSource(geoJsonSource);
        this.textMapboxLayer?.updateSelectionFilter();
    }

    /** When in the preview we only show the layer's altitude */
    private createModelAltitudeExpression(modelAltitude: MapModuleLayer["modelStyle"]["modelAltitude"]): ModelLayerSpecification["paint"]["model-translation"] {
        const altitudeValue = getStaticStyleValue(modelAltitude) ?? 0;
        if (this.preview) {
            return [0, 0, altitudeValue];
        }
        return [0, 0, ["+", ["coalesce", ["get", this.altitudeDataField.id], 0], altitudeValue]];
    }

    public updateMapOrder(aboveLayerId: string): void {
        super.updateMapOrder(aboveLayerId);
        this.standStemMapboxLayer.updateMapOrder();
        this.standBaseMapboxLayer.updateMapOrder();
        this.textMapboxLayer?.updateMapOrder(aboveLayerId);
    }

    public onDrawingStarted(objectIds: string[]): void {
        super.onDrawingStarted(objectIds);
        this.textMapboxLayer?.onDrawingStarted(objectIds);
    }

    public onDrawingFinished(objectIds: string[]): void {
        super.onDrawingFinished(objectIds);
        this.textMapboxLayer?.onDrawingFinished(objectIds);
    }

    public hideLayer(): void {
        super.hideLayer();
        this.textMapboxLayer?.hideLayer();
    }

    public showLayer(): void {
        super.showLayer();
        this.textMapboxLayer?.showLayer();
    }

    public getBottomMostLayerId() {
        return this.standBaseMapboxLayer?.layerId;
    }

    public removeLayer(): void {
        super.removeLayer();
        this.stateSubscription?.unsubscribe();
        this.standBaseMapboxLayer?.removeLayer();
        this.standStemMapboxLayer?.removeLayer();
        this.textMapboxLayer?.removeLayer();
    }

    public removeLocalSource(): void {
        super.removeLocalSource();
        this.standBaseMapboxLayer?.removeLocalSource();
        this.standStemMapboxLayer?.removeLocalSource();
        this.textMapboxLayer?.removeLocalSource();
    }
}
